import type { KycDuplicateAccountHintPayload, KYCStatusMessage } from './kyc.schema';
import { useQueryClient } from '@tanstack/react-query';
import { pushCustomGtmEventToDataLayer } from '@/components/entities/analytics/gtm';
import { useSocketEvent } from '@/hooks/socket/useSocketEvent';
import { getStateConfig } from '@/modals/modules/global/+KycStateModal/consts';
import openModal from '@/modals/utils/openModal';
import { QueriesKeys } from '@/queries/queriesKeys';
import { tokenManager } from '@/queries/token-manager';
import { useAppSelector } from '@/redux/hooks';
import { preloadVideoSources } from '@/utils/preloadVideoSources';
import { KycDuplicateAccountHintPayloadSchema, KYCStatusMessageSchema } from './kyc.schema';

export function useKYCSocket() {
  const queryClient = useQueryClient();
  const userSession = useAppSelector(state => state.user.userSession);

  useSocketEvent<KYCStatusMessage>(
    'kyc.status.update',
    (raw) => {
      const parsed = KYCStatusMessageSchema.safeParse(raw);
      if (!parsed.success) {
        return;
      }

      const data = parsed.data;

      queryClient.invalidateQueries({ queryKey: [QueriesKeys.user] });
      queryClient.invalidateQueries({ queryKey: [QueriesKeys.verifications] });

      if (data.state === 'approved') {
        pushCustomGtmEventToDataLayer('user_kyc_completed');
      }

      const stateConfig = getStateConfig(data.type, data.state);
      if (!stateConfig) {
        return;
      }

      preloadVideoSources(stateConfig.videoSources);
      openModal('kyc-state', { type: data.type, state: data.state });
    },
    Boolean(userSession),
  );

  useSocketEvent<KycDuplicateAccountHintPayload>(
    'kyc.duplicate-account-hint',
    (raw) => {
      const parsed = KycDuplicateAccountHintPayloadSchema.safeParse(raw);
      if (!parsed.success) {
        return;
      }

      const currentUserId = tokenManager.getUserId();
      if (currentUserId && parsed.data.userId !== currentUserId) {
        return;
      }

      openModal('kyc-duplicate-account-hint', {
        matchedApprovedEmail: parsed.data.matchedApprovedEmail,
      });
    },
    Boolean(userSession),
  );
}
